import {
  type IMembershipBenefitGroup,
  type IMembershipLevel,
  type IMembershipPlan,
  type MembershipBillingType,
} from "../interfaces/membership.interface";

const PLAN_COLOR_BY_CODE: Record<string, string> = {
  TRADER: "#6e8968",
  TRADING: "#6e8968",
  BASIC: "#009d85",
  MIDDLE: "#ce5d31",
  PRO: "#d39919",
};

const FALLBACK_COLORS = ["#6e8968", "#009d85", "#ce5d31", "#d39919", "#3b82f6"];

const BILLING_LABEL_BY_TYPE: Record<MembershipBillingType, string> = {
  MONTHLY: "Monthly",
  ANNUAL: "Yearly",
};

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const toBenefitGroup = (
  title: string,
  items?: string[] | null
): IMembershipBenefitGroup | null => {
  if (!items || items.length === 0) {
    return null;
  }

  return {
    title,
    items: items.map((item) => ({
      label: item,
      enabled: true,
    })),
  };
};

export const sortMembershipLevels = (
  levels: IMembershipLevel[]
): IMembershipLevel[] =>
  [...levels].sort((a, b) => {
    const aLevel = a.level ?? a.config?.level ?? Number.MAX_SAFE_INTEGER;
    const bLevel = b.level ?? b.config?.level ?? Number.MAX_SAFE_INTEGER;

    if (aLevel !== bLevel) {
      return aLevel - bLevel;
    }

    return a.name.localeCompare(b.name);
  });

export const mapMembershipLevelToPlan = (
  level: IMembershipLevel,
  billingType: MembershipBillingType,
  index: number
): IMembershipPlan => {
  const amount =
    billingType === "MONTHLY" ? level.monthlyPrice : level.annualPrice;
  const price =
    typeof amount === "number" ? usdFormatter.format(amount) : undefined;
  const billingPeriod =
    typeof amount === "number" ? BILLING_LABEL_BY_TYPE[billingType] : undefined;

  const accessItems = level.config?.access ?? level.access;

  const benefits = [
    toBenefitGroup("Access", accessItems),
    toBenefitGroup("Education", level.education),
    toBenefitGroup("Fees", level.fees),
    toBenefitGroup(
      "Cost",
      Array.isArray(level.cost)
        ? level.cost
        : typeof level.cost === "number"
          ? [usdFormatter.format(level.cost)]
          : undefined
    ),
  ].filter(Boolean) as IMembershipBenefitGroup[];

  const colorKey = level.planCode?.toUpperCase();
  const levelIndex = level.level ?? level.config?.level ?? index;
  const logoColor =
    (colorKey ? PLAN_COLOR_BY_CODE[colorKey] : undefined) ??
    FALLBACK_COLORS[(index + levelIndex) % FALLBACK_COLORS.length];

  return {
    id: level.id,
    planCode: level.planCode,
    name: level.name,
    level: level.level ?? level.config?.level,
    monthlyPrice: level.monthlyPrice,
    annualPrice: level.annualPrice,
    annualMonthlyPrice: level.annualMonthlyPrice,
    cost: level.cost,
    setupFee: level.setupFee,
    description: level.description,
    access: accessItems,
    education: level.education,
    fees: level.fees,
    config: level.config,
    price,
    billingPeriod,
    benefits,
    logoColor,
    billingType,
    amount,
    currency: "USD",
    isPopular: level.isPopular,
  };
};

export const BILLING_LABELS = BILLING_LABEL_BY_TYPE;
